/**
 * Azure Blob Storage Service
 * 
 * Implementation of IBlobStorageService for Azure Blob Storage.
 * Uses HTTP Range requests for efficient chunked downloads.
 */

import type {
  BlobDownloadOptions,
  BlobMetadata,
  ChunkDownloadResult,
  IBlobStorageService
} from './types';

// Azure-optimized chunk size: 2MB for frequent progress updates
// Azure serves byte ranges efficiently with no re-computation
const AZURE_DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024; // 2MB

// Default concurrency for parallel downloads
const AZURE_DEFAULT_CONCURRENCY = 4;

/**
 * Azure Blob Storage service implementation.
 * Optimized for Azure's efficient Range request handling.
 */
export class AzureBlobStorageService implements IBlobStorageService {
  readonly provider = 'azure' as const;

  private defaultChunkSize: number;
  private defaultConcurrency: number;

  constructor(options?: { chunkSize?: number; maxConcurrency?: number; }) {
    this.defaultChunkSize = options?.chunkSize ?? AZURE_DEFAULT_CHUNK_SIZE;
    this.defaultConcurrency = options?.maxConcurrency ?? AZURE_DEFAULT_CONCURRENCY;
  }

  async getMetadata(url: string): Promise<BlobMetadata | null> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HEAD request failed: ${response.status} ${response.statusText}`);
      }

      const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
      const contentType = response.headers.get('Content-Type') || undefined;
      const etag = response.headers.get('ETag') || undefined;
      const lastModifiedStr = response.headers.get('Last-Modified');
      const lastModified = lastModifiedStr ? new Date(lastModifiedStr) : undefined;

      return {
        url,
        contentLength,
        contentType,
        etag,
        lastModified,
      };
    } catch (error) {
      console.error('[AzureBlobStorage] Failed to get metadata:', error);
      return null;
    }
  }

  async downloadRange(url: string, startByte: number, endByte: number): Promise<ChunkDownloadResult> {
    const response = await fetch(url, {
      headers: {
        'Range': `bytes=${startByte}-${endByte}`,
      },
      cache: 'no-store',
    });

    // 206 Partial Content is expected for Range requests
    // 200 OK means server ignored Range header and returned full file
    if (!response.ok && response.status !== 206) {
      throw new Error(`Range request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.arrayBuffer();

    // Parse Content-Range header: "bytes start-end/total"
    let totalSize: number | undefined;
    let isLastChunk = false;

    const contentRange = response.headers.get('Content-Range');
    if (contentRange) {
      const match = contentRange.match(/bytes\s+(\d+)-(\d+)\/(\d+)/);
      if (match) {
        const rangeEnd = parseInt(match[2], 10);
        totalSize = parseInt(match[3], 10);
        isLastChunk = rangeEnd >= totalSize - 1;
      }
    } else if (response.status === 200) {
      // Server returned full file (ignored Range header)
      totalSize = data.byteLength;
      isLastChunk = true;
    }

    return {
      data,
      startByte,
      endByte: startByte + data.byteLength - 1,
      totalSize,
      isLastChunk,
    };
  }

  async downloadBlob(url: string, options?: BlobDownloadOptions): Promise<ArrayBuffer> {
    const chunkSize = options?.chunkSize ?? this.defaultChunkSize;
    const maxConcurrency = options?.maxConcurrency ?? this.defaultConcurrency;
    const onProgress = options?.onProgress;
    const signal = options?.signal;

    console.log(`[AzureBlobStorage] Starting download: ${url}`);
    console.log(`[AzureBlobStorage] Chunk size: ${chunkSize}, Max concurrency: ${maxConcurrency}`);

    // Get first chunk to determine total size
    onProgress?.({
      phase: 'initializing',
      totalBytes: 0,
      bytesDownloaded: 0,
      currentChunk: 0,
      totalChunks: 0,
      percent: 0,
    });

    const firstResult = await this.downloadRange(url, 0, chunkSize - 1);

    if (firstResult.totalSize === undefined) {
      throw new Error('Server did not return Content-Range header with total size');
    }

    const totalSize = firstResult.totalSize;
    const totalChunks = Math.ceil(totalSize / chunkSize);

    console.log(`[AzureBlobStorage] Total size: ${totalSize} bytes, ${totalChunks} chunks`);

    // If only one chunk, we're done
    if (totalChunks === 1) {
      onProgress?.({
        phase: 'complete',
        totalBytes: totalSize,
        bytesDownloaded: firstResult.data.byteLength,
        currentChunk: 1,
        totalChunks: 1,
        percent: 100,
      });
      return firstResult.data;
    }

    // Store results in order
    const chunkResults: ArrayBuffer[] = new Array(totalChunks);
    chunkResults[0] = firstResult.data;

    let completedChunks = 1;
    let bytesDownloaded = firstResult.data.byteLength;

    onProgress?.({
      phase: 'downloading',
      totalBytes: totalSize,
      bytesDownloaded,
      currentChunk: completedChunks,
      totalChunks,
      percent: Math.round((bytesDownloaded / totalSize) * 100),
    });

    // Download remaining chunks in parallel
    const downloadChunk = async (chunkIndex: number): Promise<void> => {
      if (signal?.aborted) {
        throw new Error('Download aborted');
      }

      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize - 1, totalSize - 1);

      const result = await this.downloadRange(url, start, end);
      chunkResults[chunkIndex] = result.data;
      completedChunks++;
      bytesDownloaded += result.data.byteLength;

      const percent = Math.round((bytesDownloaded / totalSize) * 100);
      onProgress?.({
        phase: 'downloading',
        totalBytes: totalSize,
        bytesDownloaded,
        currentChunk: completedChunks,
        totalChunks,
        percent,
      });
    };

    // Process remaining chunks with concurrency limit
    const pendingPromises: Promise<void>[] = [];
    let nextChunk = 1;

    const startNextChunk = (): Promise<void> | null => {
      if (nextChunk >= totalChunks) return null;
      const chunkIndex = nextChunk++;
      return downloadChunk(chunkIndex);
    };

    // Start initial batch
    for (let i = 0; i < Math.min(maxConcurrency, totalChunks - 1); i++) {
      const promise = startNextChunk();
      if (promise) pendingPromises.push(promise);
    }

    // Process as chunks complete
    while (pendingPromises.length > 0) {
      if (signal?.aborted) {
        throw new Error('Download aborted');
      }

      const completedIndex = await Promise.race(
        pendingPromises.map((p, i) => p.then(() => i))
      );
      pendingPromises.splice(completedIndex, 1);

      const nextPromise = startNextChunk();
      if (nextPromise) {
        pendingPromises.push(nextPromise);
      }
    }

    console.log(`[AzureBlobStorage] Download complete: ${bytesDownloaded} bytes in ${totalChunks} chunks`);

    onProgress?.({
      phase: 'complete',
      totalBytes: totalSize,
      bytesDownloaded,
      currentChunk: totalChunks,
      totalChunks,
      percent: 100,
    });

    // Combine all chunks
    return this.combineArrayBuffers(chunkResults);
  }

  async exists(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  private combineArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
    const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;

    for (const buffer of buffers) {
      result.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }

    return result.buffer;
  }
}

